import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, Trophy, Users, ListChecks, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft } from "lucide-react";

const TYPES = [
  { key: "tournament", label: "Tournament", icon: Trophy, desc: "Upload an entire tournament — creates a tournament page + champion." },
  { key: "league", label: "League Weekend", icon: Users, desc: "Upload a weekend/round for a league (CPBL, BCPBL, EBLO…)." },
  { key: "season", label: "Full Season", icon: ListChecks, desc: "Import thousands of games with per-row game type & league." },
];
const TEMPLATES = {
  tournament: "date,home_team,away_team,home_score,away_score,home_hits,away_hits,home_errors,away_errors,round\n2026-05-10,Toronto Mets 18U,Ontario Nationals 18U,5,3,8,6,1,2,pool\n2026-05-11,Toronto Mets 18U,Ontario Blue Jays 18U,4,2,7,5,0,1,final\n",
  league: "date,home_team,away_team,home_score,away_score,home_hits,away_hits,home_errors,away_errors\n2026-05-10,Toronto Mets 18U,Ontario Nationals 18U,5,3,8,6,1,2\n",
  season: "date,home_team,away_team,home_score,away_score,home_hits,away_hits,home_errors,away_errors,game_type,league,round\n2026-05-10,Toronto Mets 18U,Ontario Nationals 18U,5,3,8,6,1,2,league,CPBL,\n2026-06-01,Toronto Mets 18U,Ontario Blue Jays 18U,7,2,10,4,1,3,tournament_playoff,,final\n",
};

export default function AdminImport() {
  const [type, setType] = useState("tournament");
  const [step, setStep] = useState(0);
  const [ctx, setCtx] = useState({ tournament: { name: "", location: "", organizer: "" }, tournament_dates: "", age_division: "18U", league_id: "" });
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [leagues, setLeagues] = useState([]);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/live/leagues", { params: { active: true } }).then(({ data }) => setLeagues(data)).catch(() => {}); }, []);
  const reset = () => { setStep(0); setCsv(""); setFileName(""); setPreview(null); setSummary(null); };

  const download = () => {
    const blob = new Blob([TEMPLATES[type]], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `cbr_${type}_template.csv`; a.click();
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const rd = new FileReader(); rd.onload = () => setCsv(rd.result); rd.readAsText(f);
  };
  const body = () => ({ import_type: type, csv, file_name: fileName, context: { ...ctx, tournament: type === "league" ? null : ctx.tournament, league_id: type === "league" ? ctx.league_id : null } });

  const runPreview = () => {
    if (!csv) return toast.error("Upload a CSV first");
    if (type === "tournament" && !ctx.tournament.name) return toast.error("Tournament name required");
    if (type === "league" && !ctx.league_id) return toast.error("Select a league");
    setBusy(true);
    api.post("/live/import/preview", body()).then(({ data }) => { setPreview(data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Preview failed")).finally(() => setBusy(false));
  };
  const runImport = () => {
    setBusy(true);
    api.post("/live/import/commit", body()).then(({ data }) => { setSummary(data); toast.success(`${data.games_imported} games imported`); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Import failed")).finally(() => setBusy(false));
  };

  const wizardSteps = ["Name", "Location", "Dates", "Age Division", "Upload"];
  const canNext = () => (step === 0 ? ctx.tournament.name.trim() : true);

  return (
    <AdminLayout>
    <div className="max-w-3xl mx-auto p-6" data-testid="admin-import">
      <div className="label-overline text-[#E63946]">CBR Live</div>
      <h1 className="font-display text-3xl uppercase tracking-tight text-[#0A192F]">Bulk Results Import</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">Populate CBR Team Rankings from any source — every game feeds the same engine.</p>

      {/* Type selector */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {TYPES.map((t) => (
          <button key={t.key} data-testid={`import-type-${t.key}`} onClick={() => { setType(t.key); reset(); }}
            className={`text-left p-4 rounded-lg border transition-colors ${type === t.key ? "border-[#E63946] bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <t.icon className={`w-5 h-5 ${type === t.key ? "text-[#E63946]" : "text-slate-400"}`} />
            <div className="text-sm font-semibold text-[#0A192F] mt-2">{t.label}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{t.desc}</div>
          </button>
        ))}
      </div>

      <button data-testid="download-template" onClick={download} className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-[#E63946] mb-4">
        <Download className="w-4 h-4" /> Download {type} CSV template
      </button>
      <Link to="/admin/live/import-history" data-testid="import-history-link" className="ml-4 text-xs font-semibold text-slate-500 hover:text-[#E63946]">View Import History →</Link>

      {summary ? <Summary summary={summary} type={type} onReset={() => { reset(); }} />
        : preview ? <Preview preview={preview} busy={busy} onBack={() => setPreview(null)} onImport={runImport} />
        : (
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            {type === "tournament" ? (
              <>
                <Stepper steps={wizardSteps} step={step} />
                {step === 0 && <Field label="Tournament Name" testid="wiz-name" value={ctx.tournament.name} onChange={(v) => setCtx({ ...ctx, tournament: { ...ctx.tournament, name: v } })} />}
                {step === 1 && <Field label="Location" testid="wiz-location" value={ctx.tournament.location} onChange={(v) => setCtx({ ...ctx, tournament: { ...ctx.tournament, location: v } })} />}
                {step === 2 && <Field label="Dates (e.g. May 10–12, 2026)" testid="wiz-dates" value={ctx.tournament_dates} onChange={(v) => setCtx({ ...ctx, tournament_dates: v })} />}
                {step === 3 && (
                  <div><label className="label-overline">Age Division</label>
                    <select data-testid="wiz-division" value={ctx.age_division} onChange={(e) => setCtx({ ...ctx, age_division: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1">
                      {["18U", "16U", "15U", "14U", "13U"].map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <Field label="Organizer (optional)" value={ctx.tournament.organizer} onChange={(v) => setCtx({ ...ctx, tournament: { ...ctx.tournament, organizer: v } })} />
                  </div>
                )}
                {step === 4 && <UploadBox onFile={onFile} fileName={fileName} />}
                <div className="flex justify-between pt-2">
                  <button disabled={step === 0} onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-sm font-semibold text-slate-500 disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
                  {step < 4 ? <button data-testid="wiz-next" disabled={!canNext()} onClick={() => setStep(step + 1)} className="flex items-center gap-1 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider px-4 py-2 rounded-sm disabled:opacity-40">Next <ArrowRight className="w-4 h-4" /></button>
                    : <button data-testid="preview-btn" disabled={busy || !csv} onClick={runPreview} className="bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-5 py-2 rounded-sm disabled:opacity-50">Preview Import</button>}
                </div>
              </>
            ) : (
              <>
                {type === "league" && (
                  <div><label className="label-overline">League</label>
                    <select data-testid="import-league" value={ctx.league_id} onChange={(e) => setCtx({ ...ctx, league_id: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1">
                      <option value="">Select league…</option>
                      {leagues.map((l) => <option key={l.id} value={l.id}>{l.abbreviation ? `${l.abbreviation} — ` : ""}{l.name}</option>)}
                    </select>
                  </div>
                )}
                <UploadBox onFile={onFile} fileName={fileName} />
                <button data-testid="preview-btn" disabled={busy || !csv} onClick={runPreview} className="w-full bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm disabled:opacity-50">Preview Import</button>
              </>
            )}
          </div>
        )}
    </div>
    </AdminLayout>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div><label className="label-overline">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1" /></div>
);
const UploadBox = ({ onFile, fileName }) => (
  <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg py-8 cursor-pointer hover:border-[#E63946]" data-testid="upload-box">
    <FileSpreadsheet className="w-8 h-8 text-slate-300" />
    <span className="text-sm text-slate-500 mt-2">{fileName || "Click to upload results CSV"}</span>
    <input type="file" accept=".csv,text/csv" data-testid="csv-file" onChange={onFile} className="hidden" />
  </label>
);
const Stepper = ({ steps, step }) => (
  <div className="flex items-center gap-1 mb-2">
    {steps.map((s, i) => (
      <div key={s} className="flex items-center gap-1 flex-1">
        <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold ${i <= step ? "bg-[#E63946] text-white" : "bg-slate-200 text-slate-400"}`}>{i + 1}</div>
        {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-[#E63946]" : "bg-slate-200"}`} />}
      </div>
    ))}
  </div>
);

function Preview({ preview, busy, onBack, onImport }) {
  const stat = (label, val, cls) => <div className={`p-3 rounded-sm text-center ${cls}`}><div className="font-data text-2xl">{val}</div><div className="text-[10px] uppercase tracking-wider">{label}</div></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="import-preview">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {stat("Games Found", preview.games_found, "bg-slate-50 text-[#0A192F]")}
        {stat("New Teams", preview.new_teams.length, "bg-emerald-50 text-emerald-700")}
        {stat("Existing", preview.existing_teams.length, "bg-slate-50 text-slate-600")}
        {stat("Duplicates", preview.duplicates.length, "bg-amber-50 text-amber-700")}
        {stat("Errors", preview.errors.length, "bg-rose-50 text-rose-700")}
      </div>
      {preview.new_teams.length > 0 && <Detail title="New teams to create" items={preview.new_teams} />}
      {preview.duplicates.length > 0 && <div data-testid="dup-warning" className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-sm"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {preview.duplicates.length} likely duplicate game(s) detected (same date, teams &amp; score). They will be skipped on import.</div>}
      {preview.errors.length > 0 && <div className="text-sm text-rose-700 bg-rose-50 p-3 rounded-sm">{preview.errors.slice(0, 6).map((e, i) => <div key={i}>Line {e.line}: {e.error}</div>)}{preview.errors.length > 6 && <div>+{preview.errors.length - 6} more…</div>}</div>}
      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="text-sm font-semibold text-slate-500">Back</button>
        <button data-testid="commit-btn" disabled={busy || preview.games_found === 0} onClick={onImport} className="bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-6 py-2 rounded-sm disabled:opacity-50">Import {preview.games_found} Games</button>
      </div>
    </div>
  );
}
const Detail = ({ title, items }) => (
  <div><div className="label-overline mb-1">{title}</div><div className="flex flex-wrap gap-1">{items.slice(0, 30).map((n) => <span key={n} className="text-[11px] bg-slate-100 px-2 py-0.5 rounded-sm">{n}</span>)}</div></div>
);

function Summary({ summary, type, onReset }) {
  const row = (label, val) => <div className="flex justify-between py-2 border-b border-slate-50 text-sm"><span className="text-slate-500">{label}</span><span className="font-data text-[#0A192F]">{val}</span></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="import-summary">
      <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-500" /><h2 className="font-display text-2xl uppercase tracking-tight">Import Complete</h2></div>
      {row("Games Imported", summary.games_imported)}
      {row("Teams Created", summary.teams_created)}
      {row("Teams Updated", summary.teams_updated)}
      {row("Duplicates Skipped", summary.duplicates_skipped)}
      {row("Errors", summary.errors.length)}
      {row("Ratings Updated", summary.ratings_updated)}
      {row("Time Required", `${summary.time_seconds}s`)}
      <div className="flex gap-3 mt-4">
        {summary.tournament_id && <Link to={`/live/tournaments/${summary.tournament_id}`} data-testid="view-tournament" className="flex-1 text-center bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm">View Tournament Page</Link>}
        <Link to="/live/team-rankings" className="flex-1 text-center border border-slate-200 text-[#0A192F] text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm">View Rankings</Link>
        <button data-testid="import-another" onClick={onReset} className="flex-1 border border-slate-200 text-slate-600 text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm">Import Another</button>
      </div>
    </div>
  );
}
